// §11.7 (D-276) — the desktop's icon grid and its context menu, once, for the two skins that
// have one.
//
// It lives in `host/` rather than in a skin because §6.5 forbids a skin importing another, and two
// independent copies of a drag-and-drop grid is exactly the shape D-263 was about: a change lands
// in one and the other quietly diverges. The skins keep their own **look** — every class name here
// is built from a prefix each one passes in, so their sheets go on styling `.house-desktop-icon`
// and `.win11-desktop-icon` exactly as they already did.
//
// The macOS skin has no grid and does not mount this (John, 2026-08-06): its desktop is a place
// files go, and its apps live in the Dock and Launchpad.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { parentOf, type VfsPath } from '@hangar/shared';
import { File, Folder } from '@/assets/icons';
import { COPY } from '@/copy';
import { useDesktopItems, useKernel, useSetting } from '@/kernel/react';
import type { DesktopItem } from '@/kernel/desktop';

/** The drag payload. A path is enough — the model is the filesystem, so there is nothing else. */
export const DESKTOP_DRAG_TYPE = 'application/x-hangar-desktop-item';

/**
 * A path no icon has, used as the drop target for the empty space past the last icon.
 *
 * It only ever reaches `setOver`, never the service — the grid's own drop handler turns it into
 * `reorder(source, null)`, which means "put it last".
 */
const END_OF_GRID = '\u0000end' as VfsPath;

/** Which third of the icon the pointer is in: the outer quarters rearrange, the middle groups. */
function edgeOf(event: DragEvent): 'into' | 'before' | 'after' {
  const box = event.currentTarget.getBoundingClientRect();
  if (box.width === 0) return 'into';
  const across = (event.clientX - box.left) / box.width;
  if (across < 0.25) return 'before';
  if (across > 0.75) return 'after';
  return 'into';
}

/** The icon after `path` in display order, or `null` when it is the last one. */
function nextAfter(items: readonly DesktopItem[], path: VfsPath): VfsPath | null {
  const at = items.findIndex((item) => item.path === path);
  return at < 0 ? null : (items[at + 1]?.path ?? null);
}

interface MenuState {
  x: number;
  y: number;
  /** The icon that was right-clicked, or `null` for the surface itself. */
  item: DesktopItem | null;
}

export interface DesktopIconsProps {
  /** `house` or `win11` — the prefix every class name below is built from. */
  prefix: string;
  /** §6.3 — each skin calls its launcher something different. */
  launcherLabel: string;
}

export function DesktopIcons({ prefix, launcherLabel }: DesktopIconsProps) {
  const kernel = useKernel();
  const iconSize = useSetting('desktopIconSize');
  /** `undefined` is the desktop itself; a path is a folder the user opened into. */
  const [open, setOpen] = useState<VfsPath | undefined>(undefined);
  const items = useDesktopItems(open);
  /**
   * What a release would do right now, so the answer is visible *before* the mouse comes up.
   *
   * `into` groups or moves in; `before`/`after` rearrange. Which one you get depends on where in
   * the icon the pointer is — the outer quarters rearrange, the middle half groups (D-287). Both
   * gestures have to live on the same target, and a drop that silently did the wrong one of the
   * two would be much worse than one that shows which it is about to do.
   */
  const [over, setOver] = useState<{ path: VfsPath; mode: 'into' | 'before' | 'after' } | null>(null);
  /** The icon whose label is currently an input. */
  const [editing, setEditing] = useState<VfsPath | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  /**
   * Apps not already on the desktop — the "Add app" submenu. Read when the menu opens.
   *
   * `null` means **not read yet**, and the distinction from `[]` is load-bearing (D-394). The read
   * is a recursive VFS walk that resolves after the menu has already rendered, so a single state
   * conflated the two answers and the submenu said *"Every app is already on the desktop"* while it
   * was still finding out. Worse, the walk's rejection was unhandled: one failed listing left that
   * sentence on screen permanently, and the only apps it could ever be wrong about are the ones a
   * person just removed and is trying to put back.
   */
  const [addable, setAddable] = useState<ReadonlyArray<{ id: string; name: string }> | null>(null);
  const editInput = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  // A folder that is renamed, moved or trashed while open must not leave the grid pointing at a
  // path that is no longer there — that would be an empty desktop with no way back.
  //
  // Asked of the desktop service, not of the filesystem (D-289). `vfs.stat` was the wrong
  // question: the games' group is one of the default icons, so before anything is written it has
  // no directory behind it — and this guard slammed it shut on the same tick it was opened, which
  // looked exactly like a folder that would not open.
  useEffect(() => {
    if (open === undefined) return;
    let alive = true;
    void kernel.desktop
      .isFolder(open)
      .then((ok) => {
        if (alive && !ok) setOpen(undefined);
      })
      .catch(() => {
        if (alive) setOpen(undefined);
      });
    return () => {
      alive = false;
    };
  }, [kernel, open, items]);

  useEffect(() => {
    if (editing) editInput.current?.select();
  }, [editing]);

  // The same dismissal the skins' own menus have always had: Escape, or a pointer anywhere else.
  useEffect(() => {
    if (!menu) return;
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      closeMenu();
    };
    const onPointer = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
    };
  }, [menu, closeMenu]);

  const openMenu = useCallback(
    (event: MouseEvent, item: DesktopItem | null) => {
      event.preventDefault();
      event.stopPropagation();
      setMenu({ x: event.clientX, y: event.clientY, item });
      // The previous menu's answer is not this one's: an app added or removed since would make it
      // stale, and showing a stale list is how you offer to add something that is already there.
      setAddable(null);
      // §24.11 — `launchable()` already excludes gated apps for a signed-out visitor, so the Add
      // menu can never offer one.
      void kernel.desktop
        .appsPresent()
        .then((present) =>
          kernel.launch
            .launchable()
            .filter((app) => !present.has(app.id))
            .map((app) => ({ id: app.id, name: app.name })),
        )
        // A failed walk means "we do not know", and the honest rendering of that is the same as
        // still reading — never the confident claim that there is nothing to add.
        .catch(() => null)
        .then(setAddable);
    },
    [kernel],
  );

  const activate = useCallback(
    (item: DesktopItem) => {
      if (item.kind === 'folder') {
        setOpen(item.path);
        return;
      }
      if (item.kind === 'app') {
        // §24.11 — an icon whose app is gated or gone says so rather than failing silently.
        if (item.unresolved) {
          kernel.shellui.showToast(COPY.desktop.unresolved);
          return;
        }
        kernel.launch.launch(item.appId as string);
        return;
      }
      // A document opens in whatever the file-type registry names (§9.2). `openPath` returning
      // null means nothing is registered — a toast beats a click that does nothing.
      if (kernel.openPath(item.path) === null) {
        kernel.shellui.showToast(COPY.explorer.openWithNone);
      }
    },
    [kernel],
  );

  const commitRename = useCallback(
    (path: VfsPath, value: string) => {
      setEditing(null);
      void kernel.desktop.rename(path, value);
    },
    [kernel],
  );

  /** Mark this icon as a live drop target. See the call sites for why both events need it. */
  const allowDrop = useCallback(
    (event: DragEvent, path: VfsPath, mode: 'into' | 'edge' = 'into') => {
      if (!event.dataTransfer.types.includes(DESKTOP_DRAG_TYPE)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'move';
      setOver({ path, mode: mode === 'into' ? 'into' : edgeOf(event) });
    },
    [],
  );

  const onDropOnIcon = useCallback(
    (event: DragEvent, target: DesktopItem) => {
      const source = event.dataTransfer.getData(DESKTOP_DRAG_TYPE) as VfsPath;
      const mode = edgeOf(event);
      setOver(null);
      if (!source || source === target.path) return;
      event.preventDefault();
      event.stopPropagation();
      // The outer quarters rearrange rather than group (D-287) — dropped on the left of an icon
      // the dragged one lands before it, on the right, after it.
      if (mode !== 'into') {
        const before = mode === 'before' ? target.path : nextAfter(items, target.path);
        void kernel.desktop.reorder(source, before);
        return;
      }
      void (async () => {
        // Onto a folder it is a move, and no question is asked. Onto anything else a group is
        // being made, and naming it is the whole gesture — so the prompt comes *first*, and
        // cancelling it leaves both icons exactly where they were.
        if (target.kind === 'folder') {
          await kernel.desktop.moveInto([source], target.path);
          return;
        }
        const name = await kernel.shellui.prompt(COPY.desktop.groupTitle, COPY.desktop.groupDefault);
        if (name === null) return;
        await kernel.desktop.group(source, target.path, name.trim() || COPY.desktop.groupDefault);
      })();
    },
    [kernel, items],
  );

  const cls = (suffix: string): string => `${prefix}-desktop-icon${suffix}`;
  const menuItem = `${prefix}-menu-item`;

  const crumbName = useMemo(
    () => (open === undefined ? '' : open.slice(open.lastIndexOf('/') + 1)),
    [open],
  );

  return (
    <div
      className={`${prefix}-desktop-area`}
      data-testid="desktop-area"
      onContextMenu={(event) => openMenu(event, null)}
    >
      {open === undefined ? null : (
        <div className={`${prefix}-desktop-crumb`} data-testid="desktop-folder-open">
          {/* Also a drop target, and it has to be: without it an icon can go *into* a group and
              never come out, which makes grouping a one-way trip. */}
          <button
            type="button"
            className={`${prefix}-desktop-crumb-back${
              over?.path === parentOf(open) ? ' is-drop-into' : ''
            }`}
            data-testid="desktop-folder-back"
            title={COPY.desktop.moveOut}
            onDragEnter={(event) => allowDrop(event, parentOf(open))}
            onDragOver={(event) => allowDrop(event, parentOf(open))}
            onDragLeave={() => setOver(null)}
            onDrop={(event) => {
              const source = event.dataTransfer.getData(DESKTOP_DRAG_TYPE) as VfsPath;
              setOver(null);
              if (!source) return;
              event.preventDefault();
              event.stopPropagation();
              void kernel.desktop.moveInto([source], parentOf(open));
            }}
            onClick={() =>
              setOpen(parentOf(open) === kernel.desktop.root() ? undefined : parentOf(open))
            }
          >
            ← {COPY.explorer.back}
          </button>
          <span data-testid="desktop-folder-name">{crumbName}</span>
        </div>
      )}

      <div
        className={`${prefix}-desktop-icons`}
        data-testid="desktop-icons"
        data-count={items.length}
        style={{ ['--desktop-icon-size' as string]: `${iconSize}px` }}
        onDragEnter={(event) => allowDrop(event, END_OF_GRID)}
        onDragOver={(event) => allowDrop(event, END_OF_GRID)}
        onDrop={(event) => {
          // The empty space past the last icon. Dropping there means "put it at the end", which
          // is the only way to move something *to* the end when the last icon's right edge is
          // already the last position.
          const source = event.dataTransfer.getData(DESKTOP_DRAG_TYPE) as VfsPath;
          setOver(null);
          if (!source) return;
          event.preventDefault();
          void kernel.desktop.reorder(source, null);
        }}
      >
        {items.length === 0 ? (
          // Not an error state — a desktop someone has tidied, plus the one thing that refills it.
          // Without it, deleting your last icon leaves a screen with no way forward on it.
          <p className={`${prefix}-desktop-empty`} data-testid="desktop-empty">
            {COPY.desktop.empty}
          </p>
        ) : null}
        {items.map((item) => (
          <div
            key={item.path}
            className={`${cls('')}${
              over?.path === item.path ? ` is-drop-${over.mode}` : ''
            }${item.unresolved ? ' is-unresolved' : ''}`}
            data-testid={
              // The app-keyed id is what every existing spec and the launcher already look for; a
              // file or folder has no app, so it is keyed by the label instead.
              item.kind === 'app' ? `desktop-icon-${item.appId}` : `desktop-item-${item.label}`
            }
            data-kind={item.kind}
            role="button"
            tabIndex={0}
            aria-label={item.label}
            title={item.unresolved ? COPY.desktop.unresolved : item.label}
            draggable={editing !== item.path}
            onContextMenu={(event) => openMenu(event, item)}
            onDragStart={(event) => {
              event.dataTransfer.setData(DESKTOP_DRAG_TYPE, item.path);
              event.dataTransfer.effectAllowed = 'move';
            }}
            // Both, and for a reason that costs an afternoon to find: a drop only fires if the
            // *most recent* drag event over the target was cancelled. Cancelling `dragover` alone
            // leaves a window — enter the icon, release before the next `dragover` lands, and the
            // browser fires `dragend` with no `drop` at all. The drag looks like it worked and
            // silently does nothing.
            onDragEnter={(event) => allowDrop(event, item.path, 'edge')}
            onDragOver={(event) => allowDrop(event, item.path, 'edge')}
            onDragLeave={() => setOver((current) => (current?.path === item.path ? null : current))}
            onDrop={(event) => onDropOnIcon(event, item)}
            onDoubleClick={() => activate(item)}
            onKeyDown={(event: KeyboardEvent) => {
              if (editing === item.path) return;
              if (event.key === 'Enter') {
                event.preventDefault();
                activate(item);
              }
              if (event.key === 'F2') {
                event.preventDefault();
                setEditing(item.path);
              }
            }}
          >
            <span className={cls('-art')} aria-hidden>
              {item.kind === 'app' ? (
                <span
                  className={cls('-art-svg')}
                  // Generated by us from the app id (D-043) — never third-party markup.
                  dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(item.appId as string) }}
                />
              ) : item.kind === 'folder' ? (
                <Folder />
              ) : (
                <File />
              )}
            </span>
            {editing === item.path ? (
              <input
                ref={editInput}
                className={cls('-rename')}
                data-testid="desktop-rename-input"
                defaultValue={item.label}
                onClick={(event) => event.stopPropagation()}
                onBlur={(event) => commitRename(item.path, event.currentTarget.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') commitRename(item.path, event.currentTarget.value);
                  if (event.key === 'Escape') setEditing(null);
                }}
              />
            ) : (
              <span className={cls('-label')}>{item.label}</span>
            )}
          </div>
        ))}
      </div>

      {menu ? (
        <div
          ref={menuRef}
          className={`${prefix}-context-menu`}
          data-testid="desktop-context-menu"
          style={{ left: menu.x, top: menu.y }}
          role="menu"
        >
          {menu.item ? (
            <>
              <button
                type="button"
                role="menuitem"
                className={menuItem}
                data-testid="desktop-menu-open"
                onClick={() => {
                  const target = menu.item as DesktopItem;
                  closeMenu();
                  activate(target);
                }}
              >
                {COPY.desktop.open}
              </button>
              <button
                type="button"
                role="menuitem"
                className={menuItem}
                data-testid="desktop-menu-rename"
                onClick={() => {
                  const target = menu.item as DesktopItem;
                  closeMenu();
                  setEditing(target.path);
                }}
              >
                {COPY.desktop.rename}
              </button>
              <button
                type="button"
                role="menuitem"
                className={menuItem}
                data-testid="desktop-menu-remove"
                onClick={() => {
                  const target = menu.item as DesktopItem;
                  closeMenu();
                  // §7.8 — to the Trash, never a delete. An icon removed by accident is one
                  // Recycle Bin restore away, exactly as a file deleted in Explorer is.
                  void kernel.desktop.remove(target.path);
                }}
              >
                {COPY.desktop.remove}
              </button>
              <hr className={`${prefix}-menu-separator`} />
            </>
          ) : null}

          <button
            type="button"
            role="menuitem"
            className={menuItem}
            data-testid="desktop-menu-new-folder"
            onClick={() => {
              closeMenu();
              void kernel.desktop.newFolder(COPY.desktop.newFolderName);
            }}
          >
            {COPY.desktop.newFolder}
          </button>

          <div className={`${prefix}-menu-group`} data-testid="desktop-menu-add">
            <span className={`${prefix}-menu-heading`}>{COPY.desktop.addApp}</span>
            {addable === null ? (
              <span className={`${prefix}-menu-empty`}>{COPY.desktop.addAppReading}</span>
            ) : addable.length === 0 ? (
              <span className={`${prefix}-menu-empty`}>{COPY.desktop.addAppEmpty}</span>
            ) : (
              <div className={`${prefix}-menu-scroll`}>
                {addable.map((app) => (
                  <button
                    key={app.id}
                    type="button"
                    role="menuitem"
                    className={menuItem}
                    data-testid={`desktop-menu-add-${app.id}`}
                    onClick={() => {
                      closeMenu();
                      void kernel.desktop.addApp(app.id);
                    }}
                  >
                    {app.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <hr className={`${prefix}-menu-separator`} />
          <button
            type="button"
            role="menuitem"
            className={menuItem}
            data-testid="desktop-menu-reset"
            onClick={() => {
              closeMenu();
              // §19.4 — the shell's own confirm. Everything on the desktop goes to the Recycle
              // Bin, which is recoverable, but it is still the biggest single thing this menu
              // does and it must not happen on a mis-click.
              void kernel.shellui.confirm(COPY.desktop.resetConfirm).then(async (yes) => {
                if (!yes) return;
                await kernel.desktop.reset();
                // Reload rather than re-render (John, 2026-08-06). A reset changes what the whole
                // shell has cached about the desktop — open windows, the search index, this
                // component's own `open` folder — and a fresh boot is the one way to be sure none
                // of it is left pointing at something that has just gone to the Trash.
                window.location.reload();
              });
            }}
          >
            {COPY.desktop.reset}
          </button>

          <hr className={`${prefix}-menu-separator`} />
          <button
            type="button"
            role="menuitem"
            className={menuItem}
            onClick={() => {
              closeMenu();
              kernel.shellui.openOverlay('launcher');
            }}
          >
            {launcherLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            className={menuItem}
            onClick={() => {
              closeMenu();
              kernel.launch.launch('settings');
            }}
          >
            {COPY.chrome.personalize}
          </button>
        </div>
      ) : null}
    </div>
  );
}
